<template>

  <v-app>

    <v-navigation-drawer
      v-if="isAuthenticated"
      temporary
      v-model="drawer"
      app
    >
      <v-list nav>

        <v-list-item
          ripple
          v-for="(item, i) in items"
          :key="i"
          :to="item.to">

          <v-list-item-action>
            <v-icon light>{{item.icon}}</v-icon>
          </v-list-item-action>

          <v-list-item-title>
            {{item.title}}
          </v-list-item-title>

        </v-list-item>

        <v-list-group prepend-icon="directions_run" no-action>
          <template #activator>
            <v-list-item-title>Planning runs</v-list-item-title>
          </template>
          <v-list-item v-for="run in planning_runs" :key="run.id" :to="'/run_plan/' + run.id" ripple>
            <v-list-item-title>
              {{run.name}}
            </v-list-item-title>
          </v-list-item>

          <v-list-item v-if="is_superuser" :to="'/plan_run'">
            <v-list-item-title>
              Add new
            </v-list-item-title>

            <v-list-item-action>
              <v-icon color="success" light>add</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list-group>

        <!-- PgmTools Plugins -->
        <v-list-item v-if="is_superuser" to="/ampli_var_stat">
          <v-list-item-action>
            <v-icon>mdi-clipboard-list-outline</v-icon>
          </v-list-item-action>
          <v-list-item-title>
            Var Stat
          </v-list-item-title>
        </v-list-item>

        <!-- Issues -->
        <v-list-item to="/issues">
          <v-list-item-action>
            <v-icon>mdi-email-alert-outline</v-icon>
          </v-list-item-action>
          <v-list-item-title>
            Issues...
          </v-list-item-title>
        </v-list-item>

        <!-- Django admin -->
        <v-list-item v-if="is_superuser" to="/admin">
          <v-list-item-action>
            <v-icon>mdi-hammer-wrench</v-icon>
          </v-list-item-action>
          <v-list-item-title>
            Administration...
          </v-list-item-title>
        </v-list-item>

      </v-list>

    </v-navigation-drawer>

    <v-app-bar app light v-if="isAuthenticated">

      <v-app-bar-nav-icon @click.stop="drawer = !drawer"></v-app-bar-nav-icon>

      <v-toolbar-title>
        <slot name="header">Header</slot>
      </v-toolbar-title>

      <v-spacer/>

      <slot v-if="is_superuser" name="toolbar-items"></slot>
      <v-spacer/>
      <slot name="info-items"></slot>

      <v-spacer/>

      <v-menu
        v-if="isAuthenticated"
        :close-on-content-click="false"
        v-model="menu"
        absolute
      >
        <template #activator="{ on:onMenu }">

          <v-tooltip bottom>
            <template #activator="{ on:onTooltip }">
              <v-btn text small v-on="{ ...onMenu, ...onTooltip }">
                <p class="pa-1 ma-1" v-bind:style="{fontSize: '13px'}">
                  {{fullname}}</p>
              </v-btn>
            </template>

            <template #default>
              <v-container>
                <v-row>
                  <v-col>superuser:</v-col>
                  <v-col>{{ user.is_superuser }}</v-col>
                </v-row>
                <v-row>
                  <v-col>curator:</v-col>
                  <v-col>{{ user.curator_labs.join(', ') }}</v-col>
                </v-row>
              </v-container>
            </template>
          </v-tooltip>
        </template>

        <template #default>
          <v-card :loading="loading">
            <v-progress-linear :active="loading" slot="progress" color="one_more_accent"
                               indeterminate></v-progress-linear>
            <v-card-title class="headline"> Change password:</v-card-title>
            <v-container>
              <v-alert :type="success_or_error" :value="password_alerted">
                <p>{{password_message}}</p>
              </v-alert>
              <v-text-field
                :type="'password'"
                label="Old password"
                v-model="old_password"
                :rules="[rules.required]"
              ></v-text-field>
              <v-text-field
                :type="'password'"
                label="New password"
                v-model="new_password"
                :rules="[rules.min, rules.required]"
              ></v-text-field>
              <v-text-field
                :type="'password'"
                label="Repeat new password"
                v-model="new_password2"
                :rules="[passwords_equal_rule, rules.required]"
              ></v-text-field>
            </v-container>
            <v-card-actions>
              <v-btn text :disabled="loading" @click="close">Cancel</v-btn>
              <v-spacer></v-spacer>
              <v-btn text :disabled="cannot_change" @click="changePassword()">Change</v-btn>
            </v-card-actions>
          </v-card>
        </template>
      </v-menu>

      <v-tooltip bottom>
        <template #activator="{ on }">
          <v-btn icon to="/logout" v-on="on" :disabled="!isAuthenticated">
            <v-icon>power_settings_new</v-icon>
          </v-btn>
        </template>
        <template #default>
          <span>Logout</span>
        </template>
      </v-tooltip>

    </v-app-bar>

    <v-main>
      <div style="overflow: hidden;"
           v-bind:style="{ width: (windowWidth - 0) + 'px', height: (windowHeight - 64 - 56) + 'px'}">
        <slot name="content"></slot>
      </div>

      <v-dialog v-model="error" persistent max-width="290" v-if="error">
        <v-card>
          <v-card-title class="headline">Error message:</v-card-title>
          <v-card-text>"{{error_message}}"<br>If necessary, make print-screen and contact the administrator.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green darken-1" text @click.native="error_ok"
                   :disabled="error_ok_disabled">OK
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-main>

    <v-bottom-navigation
      app
      dark
      fixed
      :value="true"
      background-color="one_more_accent"
    >
      <slot name="bottom_info"></slot>
      <v-spacer/>
      <slot name="bottom"></slot>
      <slot v-if="is_superuser" name="bottom_admin_tools"></slot>

    </v-bottom-navigation>


    <!-- messages -->
    <v-snackbar
      ref="snackbarMessages"
      :value="message_visibility"
      bottom left :timeout="-1"
    >
      <v-container>
        <v-row
          v-for="msg in message_list" :key="msg.id"
        >
          <v-col cols="12">

            <!--            <v-tooltip top open-delay="100ms">-->
            <!--              <template v-slot:activator="{ on }">-->
            <v-alert
              :type="msg.type" :dismissible="msg.type==='error'"
              @input="alertMessage_input(msg)">
              <div v-html="msg.html"
                   style="max-height: 350px; overflow: auto;">
              </div>
            </v-alert>
            <!--                <span>hallo</span>-->
            <!--              </template>-->
            <!--            </v-tooltip>-->

          </v-col>
        </v-row>
      </v-container>
    </v-snackbar>

    <!-- progress -->
    <v-dialog v-model="progress_progressing"
              :hide-overlay="!progress_progressing"
              persistent close-delay="200ms"
              max-width="450px">
      <v-card style="text-align: center;">
        <v-card-title style="word-break: break-word;">
          {{progress_message}}
        </v-card-title>

        <v-card-text>
          <div v-if="progress_value === -1">
            <v-progress-circular indeterminate>
            </v-progress-circular>
          </div>

          <div v-else>
            <v-progress-circular :value="progress_value">
            </v-progress-circular>
            <span>Progressed {{progress_value}}</span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>


  </v-app>

</template>

<script>

	import format from 'string-format'
	import {mapActions, mapGetters, mapState} from 'vuex'
	import {
		VApp,
		VNavigationDrawer, VList, VListItem, VListGroup,
		VListItemAction, VListItemTitle, VTooltip,
		VAppBar, VAppBarNavIcon, VToolbarTitle, VSpacer, VBtn, VIcon,
		VBottomNavigation,
		VMenu, VCard, VCardTitle, VCardText, VCardActions,
		VProgressLinear, VProgressCircular,
		VMain, VSnackbar, VContainer, VRow, VCol, VAlert,
		VDialog,
	} from 'vuetify'

	export default {
		name: "default-view",

		components: {
			VApp,
			VNavigationDrawer, VList, VListItem, VListGroup,
			VListItemAction, VListItemTitle, VTooltip,
			VAppBar, VAppBarNavIcon, VToolbarTitle, VSpacer, VBtn, VIcon,
			VBottomNavigation,
			VMenu, VCard, VCardTitle, VCardText, VCardActions,
			VProgressLinear, VProgressCircular,
			VMain, VSnackbar, VContainer, VRow, VCol, VAlert,
			VDialog,
		},

		data: function () {
			return {
				// показывать ли ошибку в отдельном диалоге,
				// или компонент сам реализует показывание ошибок
				timer: null,
				menu: false,
				password_alerted: false,
				success_or_error: 'success',
				password_message: '',
				loading: false,
				new_password: '',
				new_password2: '',
				old_password: '',
				drawer: false,
				windowHeight: 0,
				windowWidth: 0,
				rules: {
					required: (value) => !!value || 'Required.',
					min: (value) => value.length >= 8 || 'Min 8 characters'
				}
			}
		},
		created: function () {
			this.timerHandler()
			this.timer = setInterval(this.timerHandler, 300000)
		},

		beforeDestroy: function () {
			window.removeEventListener('resize', this.window_resize)
			clearInterval(this.timer)

		},

		mounted: function () {
			this.$bus.$on('asyncComponentLoading', this.component_loading)
			this.$bus.$on('asyncComponentLoaded', this.component_loaded)

			this.$nextTick(function () {
				window.addEventListener('resize', this.window_resize)
				this.window_resize()
			})
		},

		watch: {
			menu: function () {
				if (this.menu) {
					this.password_alerted = false
					this.loading = false
				}
			}
		},

		computed: {
			...mapState('error', {
				error: state => state.error,
				error_message: state => state.error_message,
				error_ok_disabled: state => state.error_ok_disabled
			}),

			...mapGetters({
				user: 'auth/user',

				// отображение ошибок и сообщений
				message_list: 'message/list',
				message_visibility: 'message/visibility',
				message_count: 'message/count',

				// отображение прогресса
				progress_progressing: 'progress/progressing',
				progress_message: 'progress/message',
				progress_value: 'progress/value',
			}),

			cannot_change() {
				return this.new_password2 !== this.new_password
			},

			passwords_equal_rule() {
				return this.new_password2 === this.new_password || 'Passwords are not equal'
			},

			items() {
				return [
					// http://materializecss.com/icons.html
					{title: 'Home', icon: 'home', to: '/home'}, //, visible: true}

					{
						title: 'Barcode remains',
						icon: 'line_weight',
						to: '/barcode_remains',
						// role: [ROLE_CURATOR, ROLE_ADMIN],
						// visible: this.is_curator || this.is_superuser
					},
					//{title: 'About', icon: 'picture_in_picture', to: '/about', visible: true},
				]
			},
			planning_runs() {
				return this.$store.state.runs.planning_run_names
			},

			...mapGetters(
				'auth', ['isAuthenticated', 'fullname', 'is_superuser', 'is_curator']
			)
		},

		methods: {
			...mapActions({
				progress_begin: 'progress/begin',
				progress_end: 'progress/end',
			}),

			window_resize: function () {
				this.windowHeight = window.innerHeight
				this.windowWidth = window.innerWidth
			},

			error_ok: function () {
				this.$store.dispatch('error/error_cured')
			},

			close: function () {
				this.menu = false
				this.password_alerted = false
				this.old_password = ""
				this.new_password = ""
				this.new_password2 = ""
			},

			changePassword: function () {
				this.password_alerted = false

				if (this.new_password !== this.new_password2) {
					return
				}
				this.loading = true
				let defView = this
				this.$store.dispatch(
					'auth/changePassword',
					{old_password: this.old_password, new_password: this.new_password}
				).then(function (response) {
					defView.success_or_error = 'success'
					defView.password_message = response.data.message
					defView.password_alerted = true
					defView.loading = false
					// auto close after 5 seconds
					setTimeout(defView.close, 3000)
				})
					.catch(function (error_response) {
						let error_message = null
						if (error_response.response && error_response.response.data && error_response.response.data.message) {
							error_message = error_response.response.data.message
						} else {
							error_message = error_response.message
						}
						defView.success_or_error = 'error'
						defView.password_message = error_message
						defView.password_alerted = true
						defView.loading = false
					})
			},
			timerHandler: function () {
				console.log("DefaultView.timerHandler()")
				this.$store.dispatch('others/defaultView')
			},
			isMenuItemVisible(item) {
				if (!(item.role)) {
					return true
				} else {
					console.log(format("Checking user role for menu item '{0}'.", item.title))

					// TODO: Проверить у пользователя группу, роль и т.д. в соответствии c item.role
					return true
				}
			},

			// -- messages (alerts) --

			alertMessage_input: function (msg) {
				this.$store.dispatch('message/remove', msg)
				// this.messages.splice(this.messages.indexOf(msg), 1)
			},

			// -- Handle components' loading events --

			component_loading: function () {
				console.log("component_loading()")
				this.progress_begin({message: "Loading components ..."})
			},

			component_loaded: function () {
				console.log("component_loaded()")
				this.progress_end()
			},
		}
	}
</script>

<style scoped>

</style>